import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { useIntervalWhenVisible } from "@/lib/useIntervalWhenVisible";
import { LoadErrorBanner } from "@/components/LoadErrorBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { SignOut, HandPointing, NavigationArrow, MapPinArea, UserCircle, FlagCheckered, Notebook, User, Suitcase, Car, Lock, XCircle, IdentificationCard, FileText, Plus, PencilSimple, Trash, CheckCircle, ArrowRight, Share, WhatsappLogo, CalendarBlank, ChartBar } from "@phosphor-icons/react";
import { fmtDate, fmtTime } from "@/lib/fmt";
import { openWhatsApp, buildPersonalRideShareText } from "@/lib/contact";
import { downloadPdf } from "@/lib/pdf";
import ClientNameDisplay from "@/components/ClientNameDisplay";
import PhoneActions from "@/components/PhoneActions";
import NotificationsBell from "@/components/NotificationsBell";
import DriverOnboardingHelpButton from "@/onboarding/DriverOnboardingHelpButton";
import BrandLogo from "@/components/BrandLogo";
import { useBranding } from "@/contexts/BrandingContext";
import NoShowModal from "@/components/NoShowModal";
import PushToggle from "@/components/PushToggle";
import TransportOrderModal from "@/components/TransportOrderModal";
import ReferenceBadge from "@/components/ReferenceBadge";
import MobileNavMenu from "@/components/MobileNavMenu";
import CalendarView from "@/components/CalendarView";
import CalendarEventModal from "@/components/CalendarEventModal";
import CalendarEventDetail from "@/components/CalendarEventDetail";
import DriverPayments from "@/components/DriverPayments";
import DriverStats from "@/components/DriverStats";
import PersonalRideModal from "@/components/PersonalRideModal";
import PersonalActivity from "@/components/PersonalActivity";
import GlobalDriverDashboard from "@/components/GlobalDriverDashboard";
import PersonalInvoices from "@/components/PersonalInvoices";
import PersonalInvoiceModal from "@/components/PersonalInvoiceModal";
import DriverNavButtons from "@/components/DriverNavButtons";

// Extract the first phone number found in free-form text (info_block).
// Matches + prefix then 8-15 digits with optional spaces / dots / dashes.
function extractFirstPhone(text) {
  if (!text) return null;
  const m = text.match(/(\+?\d[\d\s.\-]{7,18}\d)/);
  return m ? m[1].trim() : null;
}

function extractClientName(text) {
  if (!text) return null;
  const m = text.match(/(?:^|\n)\s*Client\s*:\s*([^\n]+)/i);
  return m ? m[1].trim() : null;
}

function rideDateTime(r) {
  // Returns a Date for sorting; falls back to far-future on parse issues.
  try {
    const d = r.pickup_date;
    const t = (r.pickup_time || "00:00").padStart(5, "0");
    const iso = `${d}T${t}:00`;
    const parsed = new Date(iso);
    return isNaN(parsed.getTime()) ? new Date(8640000000000000) : parsed;
  } catch { return new Date(8640000000000000); }
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

// Two-mode structure (2026-04-29):
//   • "sdrive"    → S.DRIVE dispatch experience (assigned rides, marketplace,
//                   interests, payments). Primary for dispatch-partner drivers.
//   • "personal"  → "Mon activité" SaaS experience (dashboard, personal rides,
//                   invoices, profile). Primary for independent drivers.
// The two experiences share state under the hood but the tab strip and the
// segmented control at the top always show the user exactly one context at a
// time, so "Mes courses" is no longer ambiguous:
//   - Inside S.DRIVE mode, "Mes courses" means *my assigned dispatch rides*.
//   - Inside Mon activité mode, "Courses" means *my private rides*.
const SDRIVE_TABS = [
  { key: "assigned",   label: "Mes courses" },
  { key: "available",  label: "Disponibles" },
  { key: "interested", label: "Mes intérêts" },
  { key: "payments",   label: "Paiements" },
];
const PERSONAL_TABS = [
  { key: "dashboard",  label: "Tableau de bord" },
  { key: "personal",   label: "Courses" },
  { key: "invoices",   label: "Factures" },
  { key: "profile",    label: "Profil" },
];
// Secondary tabs not shown in the main strip (accessible via header icons
// / mobile menu). Keeps the spec-mandated 4+4 tab count clean.
const AUX_TABS = {
  calendar: { key: "calendar", label: "Calendrier" },
  stats:    { key: "stats",    label: "Stats" },
};
const DEFAULT_TAB_FOR_MODE = { sdrive: "assigned", personal: "dashboard" };
function modeForTab(key) {
  if (SDRIVE_TABS.some((t) => t.key === key)) return "sdrive";
  if (PERSONAL_TABS.some((t) => t.key === key)) return "personal";
  return null; // calendar/stats keep the current mode
}

const FR_STATUSES = [
  { code: "er", label: "En Route", short: "ER", color: "bg-amber-500 text-black hover:bg-amber-600" },
  { code: "sp", label: "Sur Place", short: "SP", color: "bg-sky-500 text-white hover:bg-sky-600" },
  { code: "pec", label: "Prise en Charge", short: "PEC", color: "bg-orange-500 text-white hover:bg-orange-600" },
  { code: "dep", label: "Déposé", short: "DEP", color: "bg-emerald-600 text-white hover:bg-emerald-700" },
];

function getNextStatus(current) {
  const order = ["assigned", "er", "sp", "pec", "dep"];
  const idx = order.indexOf(current);
  return idx >= 0 && idx < order.length - 1 ? order[idx + 1] : null;
}

export default function DriverDashboard() {
  const { user, logout, linkedAdmin, exitDriverMode } = useAuth();
  const branding = useBranding();
  const [rides, setRides] = useState([]);
  // Two-mode structure (2026-04-29): mode controls which 4-tab group is
  // shown. `tab` is the active tab inside that mode. `calendar`/`stats` are
  // secondary screens reachable via header icons — they keep whatever mode
  // was active before opening them.
  const [mode, setMode] = useState("sdrive");
  const [tab, setTab] = useState("assigned");
  const switchMode = useCallback((m) => {
    setMode(m);
    setTab(DEFAULT_TAB_FOR_MODE[m] || "assigned");
  }, []);
  const switchTab = useCallback((key) => {
    const m = modeForTab(key);
    if (m && m !== mode) setMode(m);
    setTab(key);
  }, [mode]);
  const [loading, setLoading] = useState({});
  const [profile, setProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({});
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);
  // Client placard — persisted across re-mounts / status updates / refreshes
  // by storing the ride id in localStorage. The display reads the *latest*
  // ride snapshot from `rides`, so async refreshes don't blink the UI.
  const [clientPlacardRideId, _setClientPlacardRideId] = useState(() => {
    try { return localStorage.getItem("driver_placard_ride_id") || null; } catch { return null; }
  });
  const setClientPlacardRideId = useCallback((id) => {
    _setClientPlacardRideId(id);
    try {
      if (id) localStorage.setItem("driver_placard_ride_id", id);
      else localStorage.removeItem("driver_placard_ride_id");
    } catch {}
  }, []);
  const [noShowRide, setNoShowRide] = useState(null);
  const [transportRide, setTransportRide] = useState(null);
  // Personal rides (Phase 33) — separate backend collection, admin-invisible.
  const [personalRides, setPersonalRides] = useState([]);
  const [personalModal, setPersonalModal] = useState({ open: false, ride: null, mode: "full" });
  // Phase 2C — text-only transport order modal for personal rides (mirrors
  // the existing TransportOrderModal used on S.DRIVE dispatch rides).
  const [personalTransportRide, setPersonalTransportRide] = useState(null);
  // Phase 1 — same placard component, but bound to a personal ride id when set.
  const [personalPlacardId, setPersonalPlacardId] = useState(null);
  // Calendar state
  const [calEventModal, setCalEventModal] = useState({ open: false, mode: "personal_ride", initial: null, anchor: null });
  const [calDetail, setCalDetail] = useState(null);
  const [calRefresh, setCalRefresh] = useState(0);
  // Phase 4 — Personal invoicing. `invoiceRide` is the completed personal ride
  // selected for invoicing; the modal handles creation. `invoicesRefresh`
  // bumps the list to refetch.
  const [invoiceRide, setInvoiceRide] = useState(null);
  // Sub-tab state for the "Mes courses" tab. Defaults to "completed" so the
  // primary action (invoicing terminated rides) is always one tap away.
  const [coursesSubtab, setCoursesSubtab] = useState("completed");
  const [invoicesRefresh, setInvoicesRefresh] = useState(0);
  // Phase N1A — distinguish fetch-failed from list-legitimately-empty.
  const [loadError, setLoadError] = useState(false);

  const fetchRides = useCallback(async () => {
    try {
      const { data } = await api.get("/rides");
      setRides(data);
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
  }, []);
  const fetchPersonalRides = useCallback(async () => { try { const { data } = await api.get("/driver/personal-rides"); setPersonalRides(data || []); } catch {} }, []);
  const fetchProfile = useCallback(async () => { try { const { data } = await api.get("/auth/me"); setProfile(data); setProfileForm(data); } catch {} }, []);
  useEffect(() => { fetchRides(); }, [fetchRides]);
  // Phase 1 stability — polling pauses while the page is hidden
  // (critical on iPhone: prevents the cascade of parallel fetches
  // firing at once on screen-unlock, which used to wipe the JWT).
  useIntervalWhenVisible(fetchRides, 15000);
  useEffect(() => { if (tab === "personal") fetchPersonalRides(); }, [tab, fetchPersonalRides]);
  useEffect(() => { if (tab === "profile") fetchProfile(); }, [tab, fetchProfile]);

  // Deep-link: /driver?ride_id=XXX auto-switches to the correct tab + scrolls to it
  useEffect(() => {
    const rid = new URLSearchParams(window.location.search).get("ride_id");
    if (!rid || rides.length === 0) return;
    const r = rides.find((x) => x.id === rid);
    if (!r) return;
    if (r.assigned_driver === user?.id) switchTab("assigned");
    else if ((r.interested_drivers || []).includes(user?.id)) switchTab("interested");
    else switchTab("available");
    // Clean URL
    window.history.replaceState({}, "", "/driver");
    setTimeout(() => {
      const el = document.querySelector(`[data-ride-card="${rid}"]`);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("ring-2", "ring-amber-400"); setTimeout(() => el.classList.remove("ring-2", "ring-amber-400"), 2500); }
    }, 400);
  }, [rides, user?.id]);

  // Auto-clear the placard if the ride it was bound to has gone away
  // (driver withdrew, admin reassigned/archived, …) OR has been fully closed
  // out (completed / cancelled / archived). Status transitions inside the
  // active flow (assigned → er → sp → pec → dep) DO NOT trigger this — those
  // are exactly the states in which the driver wants to keep showing the name.
  useEffect(() => {
    if (!clientPlacardRideId || rides.length === 0) return;
    const r = rides.find((x) => x.id === clientPlacardRideId);
    const dead = !r || r.assigned_driver !== user?.id || ["completed", "cancelled", "archived"].includes(r.status);
    if (dead) setClientPlacardRideId(null);
  }, [clientPlacardRideId, rides, user?.id, setClientPlacardRideId]);

  const expressInterest = async (rideId) => {
    setLoading(p => ({ ...p, [rideId]: true }));
    try { await api.put(`/rides/${rideId}/interest`); toast.success("Intérêt enregistré"); fetchRides(); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
    finally { setLoading(p => ({ ...p, [rideId]: false })); }
  };

  const cancelInterest = async (rideId) => {
    setLoading(p => ({ ...p, [rideId]: true }));
    try { await api.delete(`/rides/${rideId}/interest`); toast.success("Intérêt annulé"); fetchRides(); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
    finally { setLoading(p => ({ ...p, [rideId]: false })); }
  };

  const updateStatus = async (rideId, status) => {
    setLoading(p => ({ ...p, [rideId]: true }));
    const frLabel = FR_STATUSES.find(s => s.code === status);
    // Best-effort: capture driver geolocation for ER/SP so admin sees where the
    // driver was when updating. Silent fail — never blocks the status change.
    const needsLocation = status === "er" || status === "sp";
    const getLoc = () => new Promise((resolve) => {
      if (!needsLocation || typeof navigator === "undefined" || !navigator.geolocation) return resolve({});
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => resolve({}),
        { maximumAge: 60000, timeout: 5000, enableHighAccuracy: false }
      );
    });
    try {
      const loc = await getLoc();
      await api.put(`/rides/${rideId}/status`, { status, ...loc });
      toast.success(frLabel ? `${frLabel.short} - ${frLabel.label}` : status);
      await fetchRides();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Échec de la mise à jour du statut";
      toast.error(`Erreur : ${msg}`);
    } finally {
      setLoading(p => ({ ...p, [rideId]: false }));
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try { const { data } = await api.put("/profile", { name: profileForm.name, phone: profileForm.phone, vehicle_model: profileForm.vehicle_model, vehicle_year: profileForm.vehicle_year, evtc_number: profileForm.evtc_number, plate_number: profileForm.plate_number }); setProfile(data); toast.success("Profil mis à jour"); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
    finally { setSaving(false); }
  };

  const changePassword = async () => {
    if (!pwForm.current_password || !pwForm.new_password) { toast.error("Remplissez les deux champs"); return; }
    setSaving(true);
    try { await api.put("/auth/change-password", pwForm); toast.success("Mot de passe modifié"); setPwForm({ current_password: "", new_password: "" }); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
    finally { setSaving(false); }
  };

  const withdrawFromRide = async (rideId) => {
    if (!window.confirm("Voulez-vous quitter cette course ?")) return;
    setLoading(p => ({ ...p, [rideId]: true }));
    try {
      await api.put(`/rides/${rideId}/withdraw`);
      toast.success("Vous avez quitté la course");
      await fetchRides();
    } catch (e) { toast.error(e.response?.data?.detail || "Impossible de quitter la course"); }
    finally { setLoading(p => ({ ...p, [rideId]: false })); }
  };

  const available = rides.filter(r => ["available", "interested"].includes(r.status) && !(r.interested_drivers || []).includes(user?.id) && r.assigned_driver !== user?.id);
  const myInterested = rides.filter(r => (r.interested_drivers || []).includes(user?.id) && r.assigned_driver !== user?.id && !["completed", "cancelled", "archived"].includes(r.status));
  const assigned = rides
    .filter(r => r.assigned_driver === user?.id && !["completed", "cancelled", "archived"].includes(r.status))
    .sort((a, b) => rideDateTime(a) - rideDateTime(b));
  const completed = rides
    .filter(r => r.assigned_driver === user?.id && r.status === "completed")
    .sort((a, b) => rideDateTime(b) - rideDateTime(a));

  // Priority buckets inside "Mes courses":
  //   1. Prochaine course  — the single next ride (chronologically first) that is not yet finished
  //   2. Courses du jour   — other rides scheduled today
  //   3. Courses à venir   — rides scheduled on later days
  const today = new Date();
  const nextRide = assigned[0] || null;
  const remainingAssigned = nextRide ? assigned.slice(1) : [];
  const todayRides = remainingAssigned.filter(r => isSameDay(rideDateTime(r), today));
  const upcomingRides = remainingAssigned.filter(r => !isSameDay(rideDateTime(r), today));

  const pf = (k) => (e) => setProfileForm(p => ({ ...p, [k]: e.target.value }));

  const handleExitDriver = async () => {
    try { await exitDriverMode(); window.location.assign("/admin"); }
    catch { toast.error("Impossible de revenir à l'espace admin"); }
  };

  return (
    <div data-testid="driver-dashboard" className="min-h-screen bg-[#050505]">
      {linkedAdmin && (
        <div data-testid="driver-mode-banner" className="bg-amber-500/15 border-b border-amber-500/40 text-amber-200">
          <div className="max-w-lg md:max-w-2xl lg:max-w-4xl mx-auto px-3 h-9 flex items-center justify-between gap-2 text-xs">
            <span className="truncate">
              Mode chauffeur · session admin <span className="font-semibold">{linkedAdmin.name || linkedAdmin.email}</span> en pause
            </span>
            <button data-testid="driver-exit-mode-btn" onClick={handleExitDriver}
              className="shrink-0 px-2 py-1 rounded bg-amber-500 text-black text-[11px] font-semibold hover:bg-amber-400">
              Quitter l'espace chauffeur
            </button>
          </div>
        </div>
      )}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#050505]/80 border-b border-zinc-800/50">
        <div className="max-w-lg md:max-w-2xl lg:max-w-4xl mx-auto px-3 h-14 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <BrandLogo className="h-6 w-auto shrink-0" />
            <div className="min-w-0">
              <h1 data-testid="driver-brand-title" className="text-base sm:text-lg font-medium text-white truncate">{branding.companyName}</h1>
              <p className="text-xs text-zinc-500 truncate">{user?.name}</p>
            </div>
          </div>

          {/* Desktop / tablet — unchanged */}
          <div className="hidden md:flex items-center gap-0.5">
            <Button data-testid="driver-calendar-btn" variant="ghost" size="sm" onClick={() => switchTab("calendar")} className={`px-2 ${tab === "calendar" ? "text-amber-400 bg-zinc-800/60" : "text-zinc-400 hover:text-white hover:bg-zinc-800"}`} title="Calendrier"><CalendarBlank size={18} /></Button>
            <Button data-testid="driver-stats-btn" variant="ghost" size="sm" onClick={() => switchTab("stats")} className={`px-2 ${tab === "stats" ? "text-amber-400 bg-zinc-800/60" : "text-zinc-400 hover:text-white hover:bg-zinc-800"}`} title="Statistiques"><ChartBar size={18} /></Button>
            <NotificationsBell />
            <DriverOnboardingHelpButton />
            <Button data-testid="driver-profile-btn" variant="ghost" size="sm" onClick={() => window.location.assign("/profile")} className="text-zinc-400 hover:text-white hover:bg-zinc-800 px-2" title="Mon profil"><UserCircle size={18} /></Button>
            <Button data-testid="driver-logout-btn" variant="ghost" size="sm" onClick={logout} className="text-zinc-400 hover:text-white hover:bg-zinc-800 px-2"><SignOut size={18} /></Button>
          </div>

          {/* Mobile — Notifications + Profile + Menu */}
          <div className="flex md:hidden items-center gap-1.5">
            <Button data-testid="driver-mobile-calendar-btn" variant="ghost" size="sm" onClick={() => switchTab("calendar")} className={`px-2 h-10 w-10 ${tab === "calendar" ? "text-amber-400 bg-zinc-800/60" : "text-zinc-400 hover:text-white hover:bg-zinc-800"}`} title="Calendrier"><CalendarBlank size={20} /></Button>
            <NotificationsBell />
            <DriverOnboardingHelpButton className="h-10 w-10" />
            <Button data-testid="driver-mobile-profile-btn" variant="ghost" size="sm" onClick={() => window.location.assign("/profile")} className="text-zinc-400 hover:text-white hover:bg-zinc-800 px-2 h-10 w-10" title="Mon profil"><UserCircle size={20} /></Button>
            <MobileNavMenu
              testid="driver-mobile-menu"
              title={branding.companyName}
              items={[
                { icon: ChartBar, label: "Statistiques", onClick: () => switchTab("stats"), testid: "m-driver-stats" },
                { icon: SignOut, label: "Déconnexion", tone: "danger", onClick: logout, testid: "m-driver-logout" },
              ]}
            />
          </div>
        </div>
      </header>
      <div className="sticky top-14 z-20 bg-[#050505] border-b border-zinc-800/50">
        <div className="max-w-lg md:max-w-2xl lg:max-w-4xl mx-auto px-4 pt-3 pb-0 space-y-2">
          {/* Two-mode segmented control — makes "Mes courses" unambiguous:
              in S.DRIVE mode it's dispatch-assigned rides, in Mon activité
              it becomes "Courses" meaning the driver's private rides. */}
          <div data-testid="driver-mode-switch" className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-[#121212] border border-zinc-800">
            <button
              data-testid="mode-sdrive"
              onClick={() => switchMode("sdrive")}
              className={`h-9 text-xs sm:text-sm font-medium rounded-md transition-colors ${
                mode === "sdrive"
                  ? "bg-amber-500 text-black shadow"
                  : "text-zinc-400 hover:text-zinc-200"
              }`}
            >{branding.companyName}</button>
            <button
              data-testid="mode-personal"
              onClick={() => switchMode("personal")}
              className={`h-9 text-xs sm:text-sm font-medium rounded-md transition-colors ${
                mode === "personal"
                  ? "bg-amber-500 text-black shadow"
                  : "text-zinc-400 hover:text-zinc-200"
              }`}
            >Mon activité</button>
          </div>
          <div className="flex overflow-x-auto">
            {(mode === "sdrive" ? SDRIVE_TABS : PERSONAL_TABS).map(t => (
              <button key={t.key} data-testid={`driver-tab-${t.key}`} onClick={() => switchTab(t.key)}
                className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === t.key ? "border-amber-500 text-amber-500" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
                {t.label}
                {t.key === "available" && available.length > 0 && <span className="ml-1.5 text-xs bg-violet-500/20 text-violet-400 px-1.5 py-0.5 rounded">{available.length}</span>}
                {t.key === "assigned" && assigned.length > 0 && <span className="ml-1.5 text-xs bg-sky-500/20 text-sky-400 px-1.5 py-0.5 rounded">{assigned.length}</span>}
              </button>
            ))}
          </div>
        </div>
      </div>
      <main className="max-w-lg md:max-w-2xl lg:max-w-4xl mx-auto px-4 py-4 space-y-4">
        <LoadErrorBanner error={loadError} onRetry={fetchRides} />
        {tab === "available" && (available.length === 0 ? <Empty text="Aucune course disponible" /> : available.map(r => (
          <LimitedCard key={r.id} ride={r}>
            <Button data-testid={`interest-ride-${r.id}`} onClick={() => expressInterest(r.id)} disabled={loading[r.id]} className="w-full bg-amber-500 text-black font-semibold hover:bg-amber-600 h-12 text-base active:scale-[0.97] mt-3">
              <HandPointing size={20} weight="fill" className="mr-2" /> Je suis intéressé
            </Button>
          </LimitedCard>
        )))}
        {tab === "interested" && (myInterested.length === 0 ? <Empty text="Aucun intérêt en cours" /> : myInterested.map(r => (
          <LimitedCard key={r.id} ride={r}>
            <Button data-testid={`cancel-interest-${r.id}`} onClick={() => cancelInterest(r.id)} disabled={loading[r.id]} variant="outline" className="w-full border-red-500/40 text-red-400 hover:bg-red-500/10 h-10 mt-3 active:scale-[0.97]">
              <XCircle size={18} className="mr-2" /> Annuler l'intérêt
            </Button>
          </LimitedCard>
        )))}
        {tab === "assigned" && (<>
          {assigned.length === 0 && completed.length === 0 && <Empty text="Aucune course assignée" />}
          {nextRide && (
            <div data-testid="driver-next-section" className="space-y-2">
              <p className="text-xs text-amber-500 uppercase tracking-wider font-semibold flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" /> Prochaine course
              </p>
              <ActiveCard highlight ride={nextRide} loading={loading[nextRide.id]} onUpdate={updateStatus} onShowName={(r) => setClientPlacardRideId(r.id)} onWithdraw={withdrawFromRide} onNoShow={setNoShowRide} onTransport={setTransportRide} />
            </div>
          )}
          {todayRides.length > 0 && (
            <div data-testid="driver-today-section" className="space-y-2 pt-2">
              <p className="text-xs text-sky-400 uppercase tracking-wider font-semibold">Courses du jour</p>
              {todayRides.map(r => <ActiveCard key={r.id} ride={r} loading={loading[r.id]} onUpdate={updateStatus} onShowName={(rr) => setClientPlacardRideId(rr.id)} onWithdraw={withdrawFromRide} onNoShow={setNoShowRide} onTransport={setTransportRide} />)}
            </div>
          )}
          {upcomingRides.length > 0 && (
            <div data-testid="driver-upcoming-section" className="space-y-2 pt-2">
              <p className="text-xs text-zinc-400 uppercase tracking-wider font-semibold">Courses à venir</p>
              {upcomingRides.map(r => <ActiveCard key={r.id} ride={r} loading={loading[r.id]} onUpdate={updateStatus} onShowName={(rr) => setClientPlacardRideId(rr.id)} onWithdraw={withdrawFromRide} onNoShow={setNoShowRide} onTransport={setTransportRide} />)}
            </div>
          )}
          {completed.length > 0 && (
            <div data-testid="driver-completed-section" className="pt-4 space-y-2">
              <p className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">Courses terminées</p>
              {completed.map(r => <LimitedCard key={r.id} ride={r}>
                <Button data-testid={`driver-transport-${r.id}`} onClick={() => setTransportRide(r)} variant="outline" className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-10 mt-3"><FileText size={16} className="mr-2" /> Bon de transport</Button>
              </LimitedCard>)}
            </div>
          )}
        </>)}
        {tab === "dashboard" && (
          <div data-testid="driver-dashboard-section" className="space-y-4">
            <GlobalDriverDashboard />
            <div className="bg-[#0f0f0f] border border-zinc-800 rounded-lg p-4 space-y-2">
              <p className="text-sm font-medium text-white">Raccourcis</p>
              <p className="text-xs text-zinc-500">Démarrez rapidement une course privée ou consultez vos factures.</p>
              <div className="flex gap-2 flex-wrap pt-1">
                <Button data-testid="dashboard-new-ride" onClick={() => setPersonalModal({ open: true, ride: null, mode: "full" })}
                  className="bg-amber-500 text-black hover:bg-amber-600 font-semibold h-10">
                  <Plus size={16} className="mr-1.5" /> Nouvelle course
                </Button>
                <Button data-testid="dashboard-invoices" onClick={() => switchTab("invoices")} variant="outline"
                  className="h-10 border-zinc-700 text-zinc-200 hover:bg-zinc-800">
                  <FileText size={16} className="mr-1.5" /> Mes factures
                </Button>
              </div>
            </div>
          </div>
        )}
        {tab === "personal" && (
          <div data-testid="driver-personal-section" className="space-y-4">
            <div className="flex items-center justify-between gap-2 bg-[#0f0f0f] border border-zinc-800 rounded-lg p-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-white">Mes courses</p>
                <p className="text-[11px] text-zinc-500 italic">Dispatch (assignées) et personnelles regroupées. Marquez un bouton pour facturer.</p>
              </div>
              <Button data-testid="driver-add-personal" onClick={() => setPersonalModal({ open: true, ride: null, mode: "full" })} className="bg-amber-500 text-black hover:bg-amber-600 font-semibold h-10 shrink-0">
                <Plus size={18} className="mr-1.5" /> Nouvelle
              </Button>
            </div>

            {/* ─── Status sub-tabs ─────────────────────────────────────── */}
            {/* Pure frontend filter on ride.status. No backend change.   */}
            {(() => {
              // Status buckets shared by both ride sources. Personal rides
              // use `scheduled` for "À venir"; dispatch rides use
              // `new` / `available` / `assigned`. `dep` is included in
              // "En cours" per spec even though most flows skip it.
              const UPCOMING  = new Set(["assigned", "scheduled"]);
              const ONGOING   = new Set(["er", "sp", "pec", "dep"]);
              const COMPLETED = new Set(["completed"]);
              const dispatchRows  = (rides || []).filter(r => r && r.assigned_driver === user?.id);
              const personalRows  = personalRides || [];
              const inSet = (set, r) => set.has(r.status || "scheduled");
              const counts = {
                upcoming:  dispatchRows.filter(r => inSet(UPCOMING, r)).length  + personalRows.filter(r => inSet(UPCOMING, r)).length,
                ongoing:   dispatchRows.filter(r => inSet(ONGOING, r)).length   + personalRows.filter(r => inSet(ONGOING, r)).length,
                completed: dispatchRows.filter(r => inSet(COMPLETED, r)).length + personalRows.filter(r => inSet(COMPLETED, r)).length,
              };
              const TABS = [
                { code: "upcoming",  label: "À venir",   count: counts.upcoming  },
                { code: "ongoing",   label: "En cours",  count: counts.ongoing   },
                { code: "completed", label: "Terminées", count: counts.completed },
              ];
              return (
                <div data-testid="courses-subtabs" className="flex gap-1.5 flex-wrap">
                  {TABS.map(t => (
                    <button
                      key={t.code}
                      data-testid={`courses-subtab-${t.code}`}
                      onClick={() => setCoursesSubtab(t.code)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1.5 ${
                        coursesSubtab === t.code
                          ? "bg-amber-500 text-black border-amber-500 font-semibold"
                          : "bg-zinc-900 text-zinc-300 border-zinc-700 hover:bg-zinc-800"
                      }`}
                    >
                      <span>{t.label}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${coursesSubtab === t.code ? "bg-black/20" : "bg-zinc-800 text-zinc-400"}`}>
                        {t.count}
                      </span>
                    </button>
                  ))}
                </div>
              );
            })()}

            {/* ─── Active filter sets used by both sub-renderers below ── */}
            {(() => null)()}

            {/* ─── Dispatch rides (read-only + invoice action) ─── */}
            {/* Already scoped server-side: /rides returns only rides       */}
            {/* where assigned_driver == current driver.id. No company leak. */}
            {(() => {
              const UPCOMING  = ["assigned"];
              const ONGOING   = ["er", "sp", "pec", "dep"];
              const COMPLETED = ["completed"];
              const ACTIVE = coursesSubtab === "upcoming"  ? UPCOMING
                           : coursesSubtab === "ongoing"   ? ONGOING
                           : COMPLETED;
              const dispatchAssigned = (rides || [])
                // Strict ownership filter — never show rides that are not
                // assigned to the calling driver, even if the global /rides
                // endpoint returns publicly-available rides for the
                // marketplace tab.
                .filter(r => r && r.assigned_driver === user?.id && ACTIVE.includes(r.status))
                .sort((a, b) => `${b.pickup_date} ${b.pickup_time}`.localeCompare(`${a.pickup_date} ${a.pickup_time}`));
              if (dispatchAssigned.length === 0) return null;
              return dispatchAssigned.map(r => {
                const STATUS_LABEL = { new: "Nouvelle", available: "Disponible", assigned: "Assignée", er: "En route", sp: "Sur place", pec: "Client à bord", dep: "En route final", completed: "Terminée" };
                const isDone = r.status === "completed";
                const isPab  = r.is_pab || r.payment_type === "pab";
                const paxOrClient = r.passenger_name || r.client_name || "(sans nom)";
                return (
                  <div key={`disp-${r.id}`} data-testid={`dispatch-ride-${r.id}`} className={`bg-[#121212] border ${isDone ? "border-emerald-500/20" : "border-zinc-800"} rounded-lg p-4 space-y-3`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2 min-w-0 flex-wrap">
                        <span data-testid={`dispatch-badge-${r.id}`} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 font-semibold">DISPATCH</span>
                        {isPab && (
                          <span data-testid={`dispatch-pab-${r.id}`} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-orange-500/40 bg-orange-500/10 text-orange-300">PAB</span>
                        )}
                        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${isDone ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-zinc-700 bg-zinc-900 text-zinc-300"}`}>{STATUS_LABEL[r.status] || r.status}</span>
                        {r.reference && <span className="text-[10px] text-zinc-500">#{r.reference}</span>}
                        <span className="text-sm text-zinc-200 truncate">{paxOrClient}</span>
                      </div>
                      <span className="text-xs text-zinc-500 shrink-0">{fmtDate(r.pickup_date)} · {r.pickup_time}</span>
                    </div>
                    {r.route_label && (
                      <p className="text-sm text-zinc-300 truncate">{r.route_label}</p>
                    )}
                    <div className="flex items-center justify-between gap-2 flex-wrap text-[11px] text-zinc-500">
                      <span>Net chauffeur : <span className="text-zinc-300 font-semibold">{(r.driver_amount != null ? r.driver_amount : r.driver_fare) ?? "—"} €</span></span>
                      {isDone && (
                        <Button
                          data-testid={`dispatch-invoice-${r.id}`}
                          onClick={() => setInvoiceRide({ ...r, ride_source: "dispatch" })}
                          variant="outline"
                          className="h-8 text-xs border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                        >
                          <FileText size={13} className="mr-1.5" /> Facturer
                        </Button>
                      )}
                    </div>
                    <p className="text-[10px] text-zinc-600 italic">Gérez les statuts et la navigation depuis l'onglet S.DRIVE / Mes courses.</p>
                  </div>
                );
              });
            })()}

            {/* ─── Personal rides (existing behavior, status-filtered) ─── */}
            {(() => {
              const UPCOMING  = ["scheduled"];
              const ONGOING   = ["er", "sp", "pec", "dep"];
              const COMPLETED = ["completed"];
              const ACTIVE = coursesSubtab === "upcoming"  ? UPCOMING
                           : coursesSubtab === "ongoing"   ? ONGOING
                           : COMPLETED;
              const filtered = (personalRides || []).filter(r => ACTIVE.includes(r.status || "scheduled"));
              // Phase 2D — la sous-tab "Terminées" affiche les courses les
              // plus récentes en premier (DESC). Tri local uniquement —
              // backend list_personal_rides reste en ASC pour Calendar /
              // PersonalActivity / GlobalDriverDashboard.
              if (coursesSubtab === "completed") {
                filtered.sort((a, b) =>
                  `${b.pickup_date || ""}T${b.pickup_time || ""}`
                    .localeCompare(`${a.pickup_date || ""}T${a.pickup_time || ""}`)
                );
              }
              // Combined empty-state — fired when BOTH dispatch and personal
              // lists are empty for the active sub-tab.
              const dispatchACTIVE = coursesSubtab === "upcoming"  ? ["assigned"]
                                    : coursesSubtab === "ongoing"   ? ["er","sp","pec","dep"]
                                    : ["completed"];
              const dispatchHasAny = (rides || []).some(r => r.assigned_driver === user?.id && dispatchACTIVE.includes(r.status));
              if (filtered.length === 0 && !dispatchHasAny) {
                const labels = { upcoming: "à venir", ongoing: "en cours", completed: "terminée" };
                return <Empty data-testid={`courses-empty-${coursesSubtab}`} text={`Aucune course ${labels[coursesSubtab]}`} />;
              }
              if (filtered.length === 0) return null;
              return filtered.map(r => {
                // Display-only status flow for the driver. Backend accepts the
                // same vocabulary as S.DRIVE rides so the placard component
                // can be reused 1:1.
                const STATUS_LABEL = { scheduled: "Programmée", er: "En route", sp: "Sur place", pec: "Client à bord", completed: "Terminée" };
                const NEXT = { scheduled: { code: "er",        label: "Démarrer · En route", color: "bg-amber-500 text-black hover:bg-amber-400" },
                               er:        { code: "sp",        label: "Sur place",           color: "bg-sky-500 text-white hover:bg-sky-400" },
                               sp:        { code: "pec",       label: "Client à bord",       color: "bg-orange-500 text-white hover:bg-orange-400" },
                               pec:       { code: "completed", label: "Terminer",            color: "bg-emerald-600 text-white hover:bg-emerald-500" } };
                const next = NEXT[r.status || "scheduled"];
                const phone = (r.client_phone || "").trim();
                const isDone = r.status === "completed";
                const advance = async () => {
                  if (!next) return;
                  try {
                    await api.put(`/driver/personal-rides/${r.id}/status`, { status: next.code });
                    toast.success(`Statut: ${STATUS_LABEL[next.code] || next.code}`);
                    fetchPersonalRides();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
                };
                return (
                <div key={r.id} data-testid={`personal-ride-${r.id}`} className={`bg-[#121212] border ${isDone ? "border-zinc-800" : "border-amber-500/20"} rounded-lg p-4 space-y-3`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0 flex-wrap">
                      <span data-testid={`personal-badge-${r.id}`} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-300 font-semibold">PERSONAL</span>
                      <span data-testid={`personal-status-${r.id}`} className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${isDone ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-zinc-700 bg-zinc-900 text-zinc-300"}`}>{STATUS_LABEL[r.status || "scheduled"] || r.status}</span>
                      <span className="text-sm text-zinc-200 truncate">{r.client_name || "(client sans nom)"}</span>
                    </div>
                    <span className="text-xs text-zinc-500 shrink-0">{fmtDate(r.pickup_date)} · {r.pickup_time}</span>
                  </div>
                  {(r.pickup_location || r.dropoff_location) && (
                    <p className="text-sm text-zinc-300 flex items-center gap-1.5">
                      <span className="truncate">{r.pickup_location || "—"}</span>
                      <ArrowRight size={14} className="text-zinc-600 shrink-0" />
                      <span className="truncate">{r.dropoff_location || "—"}</span>
                    </p>
                  )}
                  {r.notes && <p className="text-xs text-zinc-500 whitespace-pre-wrap">{r.notes}</p>}

                  {/* Status flow primary button — hidden once completed */}
                  {next && !isDone && (
                    <Button data-testid={`personal-status-next-${r.id}`} onClick={advance} className={`w-full font-semibold h-12 active:scale-[0.97] ${next.color}`}>
                      {next.label}
                    </Button>
                  )}

                  {/* Navigation (reused) — only while the ride is still in flight */}
                  {!isDone && <DriverNavButtons ride={r} testidPrefix={`pnav-${r.id}`} />}

                  {/* Client comms — only when phone present (per Phase 1 spec) */}
                  {phone && !isDone && (
                    <div className="grid grid-cols-3 gap-2">
                      <Button data-testid={`personal-call-${r.id}`} variant="outline" onClick={() => window.location.assign(`tel:${phone}`)} className="h-10 border-zinc-700 text-zinc-200 hover:bg-zinc-800 text-xs">📞 Appeler</Button>
                      <Button data-testid={`personal-wa-${r.id}`} variant="outline" onClick={() => openWhatsApp(phone, "")} className="h-10 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs"><WhatsappLogo size={14} className="mr-1" /> WhatsApp</Button>
                      <Button data-testid={`personal-sms-${r.id}`} variant="outline" onClick={() => window.location.assign(`sms:${phone}`)} className="h-10 border-zinc-700 text-zinc-200 hover:bg-zinc-800 text-xs">✉️ SMS</Button>
                    </div>
                  )}

                  {/* Secondary actions */}
                  <div className="flex flex-wrap gap-2">
                    {!isDone && (
                      <Button data-testid={`personal-placard-${r.id}`} onClick={() => setPersonalPlacardId(r.id)} variant="outline" className="h-9 text-xs border-zinc-700 text-zinc-200 hover:bg-zinc-800"><IdentificationCard size={14} className="mr-1.5" /> Placard</Button>
                    )}
                    <Button data-testid={`personal-share-${r.id}`} onClick={() => openWhatsApp("", buildPersonalRideShareText(r))} variant="outline" className="h-9 text-xs border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"><Share size={14} className="mr-1.5" /> Partager</Button>
                    {/* Phase 2C — Bon de transport for personal rides:
                        text-only modal, identical UX to S.DRIVE rides, but
                        backed by the driver's own profile (no S.DRIVE
                        identity leaks onto a personal ride). */}
                    <Button data-testid={`personal-voucher-${r.id}`} onClick={() => setPersonalTransportRide(r)} variant="outline" className="h-9 text-xs border-sky-500/30 text-sky-300 hover:bg-sky-500/10"><FileText size={14} className="mr-1.5" /> Bon de transport</Button>
                    <Button data-testid={`personal-notes-${r.id}`} onClick={() => setPersonalModal({ open: true, ride: r, mode: "notes" })} variant="outline" className="h-9 text-xs border-zinc-700 text-zinc-300 hover:bg-zinc-800"><Notebook size={14} className="mr-1.5" /> Notes</Button>
                    <Button data-testid={`personal-edit-${r.id}`} onClick={() => setPersonalModal({ open: true, ride: r, mode: "full" })} variant="outline" className="h-9 text-xs border-zinc-700 text-zinc-300 hover:bg-zinc-800"><PencilSimple size={14} className="mr-1.5" /> Modifier</Button>
                    {isDone && (
                      <Button data-testid={`personal-invoice-${r.id}`} onClick={() => setInvoiceRide(r)} variant="outline" className="h-9 text-xs border-amber-500/40 text-amber-300 hover:bg-amber-500/10"><FileText size={14} className="mr-1.5" /> Facturer</Button>
                    )}
                    <Button data-testid={`personal-delete-${r.id}`} onClick={async () => { if (!window.confirm("Supprimer cette course personnelle ?")) return; try { await api.delete(`/driver/personal-rides/${r.id}`); toast.success("Supprimée"); fetchPersonalRides(); setCalRefresh(x => x + 1); } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); } }} variant="outline" className="h-9 text-xs border-red-500/30 text-red-300 hover:bg-red-500/10"><Trash size={14} className="mr-1.5" /> Supprimer</Button>
                  </div>
                </div>
              );
              });
            })()}
          </div>
        )}
        {tab === "invoices" && (
          <div data-testid="driver-invoices-section" className="space-y-4">
            <div className="bg-[#0f0f0f] border border-zinc-800 rounded-lg p-3">
              <p className="text-sm font-medium text-white">Mes factures</p>
              <p className="text-[11px] text-zinc-500 italic">Générées à la demande depuis vos courses terminées. Aucune copie n'est conservée sur nos serveurs.</p>
            </div>
            <PersonalInvoices refreshKey={invoicesRefresh} />
          </div>
        )}
        {tab === "calendar" && (
          <div data-testid="driver-calendar-section" className="space-y-4">
            <CalendarView
              refreshKey={calRefresh}
              onAddPersonal={(date) => setCalEventModal({ open: true, mode: "personal_ride", initial: null, anchor: date })}
              onAddUnavailability={(date) => setCalEventModal({ open: true, mode: "unavailability", initial: null, anchor: date })}
              onEventClick={(ev) => {
                // Personal driver rides route to the PersonalRideModal in edit
                // mode (so the driver can correct details / change notes).
                // Everything else uses the existing calendar event detail panel.
                if (ev?.event_type === "driver_personal_ride" && ev.ride_id) {
                  const pr = personalRides.find(p => p.id === ev.ride_id);
                  if (pr) {
                    switchTab("personal");
                    setPersonalModal({ open: true, ride: pr, mode: "full" });
                  } else {
                    // not in cache → refetch then open
                    api.get("/driver/personal-rides").then(({ data }) => {
                      setPersonalRides(data || []);
                      const found = (data || []).find(p => p.id === ev.ride_id);
                      if (found) { switchTab("personal"); setPersonalModal({ open: true, ride: found, mode: "full" }); }
                    }).catch(() => toast.error("Course personnelle introuvable"));
                  }
                  return;
                }
                setCalDetail(ev);
              }}
            />
            <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-500">
              <span data-testid="cal-legend-sdrive" className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" />{branding.companyName}</span>
              <span data-testid="cal-legend-driver-personal" className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-violet-500" />Mes courses</span>
              <span data-testid="cal-legend-personal-event" className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-sky-500" />Course perso (manuelle)</span>
              <span data-testid="cal-legend-unavailability" className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-rose-500" />Indispo</span>
            </div>
          </div>
        )}
        {tab === "payments" && (
          <DriverPayments />
        )}
        {tab === "stats" && (
          <DriverStats />
        )}
        {tab === "profile" && profile && (
          <div className="space-y-6">
            <div className="bg-[#121212] border border-zinc-800 rounded-lg p-5 space-y-4">
              <h3 className="text-sm text-zinc-400 uppercase tracking-wider font-medium">Informations personnelles</h3>
              <PField label="Nom complet" value={profileForm.name || ""} onChange={pf("name")} />
              <PField label="Email" value={profileForm.email || ""} disabled />
              <PField label="Téléphone" value={profileForm.phone || ""} onChange={pf("phone")} placeholder="+33 6 XX XX XX XX" />
            </div>
            <div className="bg-[#121212] border border-zinc-800 rounded-lg p-5 space-y-4">
              <h3 className="text-sm text-zinc-400 uppercase tracking-wider font-medium flex items-center gap-2"><Car size={16} /> Véhicule</h3>
              <PField label="Modèle" value={profileForm.vehicle_model || ""} onChange={pf("vehicle_model")} placeholder="Tesla Model 3" />
              <PField label="Année" value={profileForm.vehicle_year || ""} onChange={pf("vehicle_year")} placeholder="2024" />
              <PField label="Numéro EVTC" value={profileForm.evtc_number || ""} onChange={pf("evtc_number")} placeholder="EVTC-XXXXX" />
              <PField label="Immatriculation" value={profileForm.plate_number || ""} onChange={pf("plate_number")} placeholder="AB-123-CD" />
              <Button data-testid="save-profile-btn" onClick={saveProfile} disabled={saving} className="w-full bg-amber-500 text-black font-semibold hover:bg-amber-600 h-11">{saving ? "Enregistrement..." : "Enregistrer le profil"}</Button>
            </div>
            <div className="bg-[#121212] border border-zinc-800 rounded-lg p-5 space-y-3">
              <h3 className="text-sm text-zinc-400 uppercase tracking-wider font-medium">Notifications push</h3>
              <p className="text-xs text-zinc-500">Recevez les nouvelles courses et assignations directement sur votre téléphone, même lorsque l'application est fermée.</p>
              <PushToggle />
            </div>
            <div className="bg-[#121212] border border-zinc-800 rounded-lg p-5 space-y-4">
              <h3 className="text-sm text-zinc-400 uppercase tracking-wider font-medium flex items-center gap-2"><Lock size={16} /> Changer le mot de passe</h3>
              <PField label="Mot de passe actuel" type="password" value={pwForm.current_password} onChange={e => setPwForm(p => ({...p, current_password: e.target.value}))} />
              <PField label="Nouveau mot de passe" type="password" value={pwForm.new_password} onChange={e => setPwForm(p => ({...p, new_password: e.target.value}))} />
              <Button data-testid="change-password-btn" onClick={changePassword} disabled={saving} className="w-full bg-zinc-800 text-white font-medium hover:bg-zinc-700 h-11">{saving ? "Modification..." : "Changer le mot de passe"}</Button>
            </div>
          </div>
        )}
      </main>
      {clientPlacardRideId && (
        <ClientNameDisplay
          rideId={clientPlacardRideId}
          rides={rides}
          onClose={() => setClientPlacardRideId(null)}
          onUpdateStatus={updateStatus}
          statusLoading={!!loading[clientPlacardRideId]}
        />
      )}
      <NoShowModal open={!!noShowRide} ride={noShowRide} onClose={() => setNoShowRide(null)} onReported={fetchRides} />
      <TransportOrderModal open={!!transportRide} ride={transportRide} onClose={() => setTransportRide(null)} />
      {/* Phase 2C — same modal, but pointed at the personal-ride endpoint. */}
      <TransportOrderModal
        open={!!personalTransportRide}
        ride={personalTransportRide}
        endpoint={personalTransportRide ? `/driver/personal-rides/${personalTransportRide.id}/transport-order` : null}
        onClose={() => setPersonalTransportRide(null)}
      />
      <PersonalRideModal open={personalModal.open} ride={personalModal.ride} mode={personalModal.mode} onClose={() => setPersonalModal({ open: false, ride: null, mode: "full" })} onSaved={() => { fetchPersonalRides(); setCalRefresh(x => x + 1); }} />
      <PersonalInvoiceModal
        open={!!invoiceRide}
        ride={invoiceRide}
        onClose={() => setInvoiceRide(null)}
        onCreated={() => { setInvoicesRefresh(x => x + 1); setInvoiceRide(null); }}
      />
      {personalPlacardId && (
        <ClientNameDisplay
          rideId={personalPlacardId}
          rides={personalRides}
          onClose={() => setPersonalPlacardId(null)}
          onUpdateStatus={async (id, st) => {
            try {
              await api.put(`/driver/personal-rides/${id}/status`, { status: st });
              fetchPersonalRides();
            } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
          }}
        />
      )}
      <CalendarEventModal
        open={calEventModal.open}
        mode={calEventModal.mode}
        initial={calEventModal.initial}
        anchorDate={calEventModal.anchor}
        onClose={() => setCalEventModal((p) => ({ ...p, open: false }))}
        onSaved={() => setCalRefresh((x) => x + 1)}
        onDeleted={() => setCalRefresh((x) => x + 1)}
      />
      <CalendarEventDetail
        event={calDetail}
        onClose={() => setCalDetail(null)}
        onEdit={(ev) => {
          setCalDetail(null);
          setCalEventModal({ open: true, mode: ev.event_type, initial: ev, anchor: null });
        }}
      />
    </div>
  );
}

function Empty({ text }) { return <div data-testid="empty-state" className="text-center py-16 text-zinc-500"><p>{text}</p></div>; }

function LimitedCard({ ride, children }) {
  const clientName = extractClientName(ride.info_block);
  return (
    <div data-testid={`ride-card-${ride.id}`} data-ride-card={ride.id} className="bg-[#121212] border border-zinc-800 rounded-lg p-4 animate-fade-in">
      <div className="flex items-start justify-between mb-2 gap-2">
        <div className="min-w-0">
          <p className="text-lg font-semibold text-white leading-tight">{ride.route_label}</p>
          {clientName && (
            <p className="text-sm text-zinc-300 mt-0.5 flex items-center gap-1.5">
              <IdentificationCard size={14} className="text-zinc-500 shrink-0" />
              <span className="truncate">{clientName}</span>
            </p>
          )}
          {ride.reference && (
            <div className="mt-1.5"><ReferenceBadge value={ride.reference} size="xs" testid={`ride-ref-${ride.id}`} /></div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-xs px-1.5 py-0.5 rounded border ${ride.ride_type === "van" ? "border-sky-500/40 text-sky-400 bg-sky-500/10" : "border-zinc-600 text-zinc-400 bg-zinc-800"}`}>{(ride.ride_type||"eco").toUpperCase()}</span>
          <StatusBadge status={ride.status} />
        </div>
      </div>
      <div className="flex items-center gap-4 text-sm text-zinc-400">
        <span className="text-white font-medium">{ride.pickup_time}</span>
        <span>{fmtDate(ride.pickup_date)}</span>
      </div>
      <div className="flex items-center gap-4 mt-2">
        <span className="text-amber-500 font-semibold text-lg">{ride.driver_fare} EUR</span>
        {ride.passenger_count > 0 && <span className="text-xs text-zinc-500 flex items-center gap-1"><User size={13} />{ride.passenger_count}</span>}
        {ride.luggage_count > 0 && <span className="text-xs text-zinc-500 flex items-center gap-1"><Suitcase size={13} />{ride.luggage_count}</span>}
      </div>
      {children}
    </div>
  );
}

function ActiveCard({ ride, loading, onUpdate, onShowName, onWithdraw, onNoShow, onTransport, highlight = false }) {
  const next = getNextStatus(ride.status);
  const nextInfo = next ? FR_STATUSES.find(s => s.code === next) : null;
  // H-24 redaction (backend-authoritative). When the server flags
  // `sensitive_hidden`, every client-identifying field on `ride` has
  // already been wiped (info_block, notes, flight_number, names,
  // phones). The frontend only decides WHERE to render the placeholder
  // — it must NEVER try to re-derive the rule from the local clock.
  const hideSensitive = !!ride.sensitive_hidden;
  const clientName = hideSensitive ? null : extractClientName(ride.info_block);
  return (
    <div
      data-testid={`ride-card-${ride.id}`}
      data-ride-card={ride.id}
      className={`rounded-lg p-4 animate-fade-in ${highlight ? "bg-gradient-to-br from-amber-500/10 via-[#121212] to-[#121212] border-2 border-amber-500/50 shadow-lg shadow-amber-500/5" : "bg-[#121212] border border-zinc-800"}`}
    >
      <div className="flex items-start justify-between mb-2 gap-2">
        <div className="min-w-0">
          <p className="text-lg font-semibold text-white leading-tight">{ride.route_label}</p>
          {clientName && (
            <p className="text-sm text-zinc-300 mt-0.5 flex items-center gap-1.5">
              <IdentificationCard size={14} className="text-zinc-500 shrink-0" />
              <span className="truncate" data-testid={`ride-client-name-${ride.id}`}>{clientName}</span>
            </p>
          )}
          {ride.reference && (
            <div className="mt-1.5"><ReferenceBadge value={ride.reference} size="xs" testid={`ride-ref-${ride.id}`} /></div>
          )}
        </div>
        <StatusBadge status={ride.status} />
      </div>
      <div className="flex items-center gap-4 text-sm text-zinc-400 mb-2">
        <span className="text-white font-medium">{ride.pickup_time}</span>
        <span>{fmtDate(ride.pickup_date)}</span>
        <span className="text-amber-500 font-semibold ml-auto">{ride.driver_fare} EUR</span>
      </div>
      {/* Phase 30 — PAB ride: also show the price the client pays. The driver
          purchased this ride themselves so the spread is theirs to know. */}
      {ride.finance?.is_pab && ride.finance?.client_price != null && (
        <div data-testid="driver-pab-client-price" className="mt-1 mb-2 text-[11px] uppercase tracking-wider px-2 py-1 inline-flex items-center gap-1.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 self-start">
          PAB · Prix client {Number(ride.finance.client_price).toFixed(2)} EUR
        </div>
      )}
      {hideSensitive ? (
        <div data-testid={`ride-sensitive-locked-${ride.id}`} className="mt-3 p-3 bg-amber-500/5 border border-amber-500/30 rounded">
          <p className="text-xs text-amber-200 leading-snug">
            Informations client disponibles 24h avant la prise en charge.
          </p>
        </div>
      ) : (
        <>
          {ride.info_block && <div className="mt-3 p-3 bg-[#1A1A1A] border border-zinc-800 rounded space-y-1"><p className="text-xs text-zinc-500 uppercase tracking-wider flex items-center gap-1"><Notebook size={12} /> Détails client</p><p className="text-sm text-zinc-300 whitespace-pre-wrap">{ride.info_block}</p>{extractFirstPhone(ride.info_block) && <div className="pt-2"><PhoneActions phone={extractFirstPhone(ride.info_block)} size="sm" withSms /></div>}</div>}
          {ride.notes && <div className="mt-2 p-2 bg-[#1A1A1A] border border-zinc-800 rounded"><p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Notes</p><p className="text-sm text-zinc-400">{ride.notes}</p></div>}
        </>
      )}
      {(ride.status_history || []).length > 0 && <div className="mt-3 space-y-1">{ride.status_history.map((h, i) => {
        // ─── Defensive rendering ──────────────────────────────────────
        // Some legacy status_history rows are stored without a `status`
        // string (Phase ≤24 entries) or with `null`. Calling
        // `.toUpperCase()` on `undefined` here used to bubble up to the
        // top-level React error boundary and blank the whole driver
        // dashboard right after a successful status update. Guard the
        // access so the row is simply skipped when the data is bad.
        const code = (h && typeof h.status === "string") ? h.status : "";
        if (!code) return null;
        const ts = h.timestamp ? fmtTime(h.timestamp) : "";
        const fr = FR_STATUSES.find(s => s.code === code);
        return <div key={i} className="flex items-center gap-2 text-xs text-zinc-500"><span className="font-semibold text-zinc-300">{fr?.short || code.toUpperCase()}</span><span>{ts}</span></div>;
      })}</div>}
      <div className="mt-3 space-y-2">
        {nextInfo && <Button data-testid={`status-${next}-${ride.id}`} onClick={() => onUpdate(ride.id, next)} disabled={loading} className={`w-full font-semibold h-14 text-base active:scale-[0.97] ${nextInfo.color}`}>
          {next === "er" && <NavigationArrow size={22} weight="fill" className="mr-2" />}
          {next === "sp" && <MapPinArea size={22} weight="fill" className="mr-2" />}
          {next === "pec" && <UserCircle size={22} weight="fill" className="mr-2" />}
          {next === "dep" && <FlagCheckered size={22} weight="fill" className="mr-2" />}
          {nextInfo.short} - {nextInfo.label}
        </Button>}
        {/* Phase 34 — Navigation buttons for assigned / in-flight S.DRIVE rides.
            Hidden for completed/cancelled to avoid useless taps after the trip. */}
        {["assigned", "er", "sp", "pec", "dep"].includes(ride.status) && (
          <DriverNavButtons ride={ride} testidPrefix={`nav-${ride.id}`} />
        )}
        <Button data-testid={`driver-transport-${ride.id}`} onClick={() => onTransport(ride)} variant="outline" disabled={hideSensitive} className="w-full border-sky-500/30 text-sky-400 hover:bg-sky-500/10 h-10 disabled:opacity-40 disabled:cursor-not-allowed" title={hideSensitive ? "Document disponible 24h avant la prise en charge" : undefined}><FileText size={16} className="mr-2" /> Bon de transport</Button>
        {ride.status === "assigned" && (
          <div className="flex flex-wrap gap-2">
            {!hideSensitive && <Button data-testid={`show-name-${ride.id}`} onClick={() => onShowName(ride)} variant="outline" className="flex-1 min-w-[48%] border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-10"><IdentificationCard size={18} className="mr-1.5" /> Nom du client</Button>}
            <Button data-testid={`no-show-${ride.id}`} onClick={() => onNoShow(ride)} variant="outline" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 h-10 px-3">No-show</Button>
            <Button data-testid={`withdraw-ride-${ride.id}`} onClick={() => onWithdraw(ride.id)} disabled={loading} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-10 px-3"><XCircle size={18} className="mr-1.5" /> Quitter</Button>
          </div>
        )}
        {["er", "sp", "pec"].includes(ride.status) && (
          <div className="flex flex-wrap gap-2 pt-1">
            {!hideSensitive && <Button data-testid={`show-name-${ride.id}`} onClick={() => onShowName(ride)} variant="outline" className="flex-1 min-w-[48%] border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-10"><IdentificationCard size={18} className="mr-1.5" /> Nom du client</Button>}
            <Button data-testid={`no-show-${ride.id}`} onClick={() => onNoShow(ride)} variant="outline" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 h-10 px-3">No-show</Button>
          </div>
        )}
      </div>
    </div>
  );
}

function PField({ label, value, onChange, disabled, placeholder, type = "text" }) {
  return <div className="space-y-1"><Label className="text-zinc-500 text-xs uppercase tracking-wider">{label}</Label><Input type={type} value={value} onChange={onChange} disabled={disabled} placeholder={placeholder} className="bg-[#1A1A1A] border-zinc-700 text-white placeholder:text-zinc-600 focus:border-amber-500 h-10 disabled:opacity-50" /></div>;
}
